import React from 'react';
import { Card, CardContent } from '../../../../components/ui/card';
import { DailySummaryProps } from './DailySummary.types';

export const DailySummary: React.FC<DailySummaryProps> = ({ data, expenses = [] }) => {
  const formatCurrency = (amount: number): string => {
    return `S/ ${amount.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate total movements (income - expenses)
  const calculateTotalMovements = (): number => {
    const totalIncome = expenses
      .filter(expense => expense.type === 'income')
      .reduce((sum, expense) => sum + expense.amount, 0);
    
    const totalExpenses = expenses
      .filter(expense => expense.type === 'expense')
      .reduce((sum, expense) => sum + expense.amount, 0);
    
    return totalIncome - totalExpenses;
  };

  // Calculate final balance: Saldo Inicial + Total Vendido + Total Movimientos
  const calculateFinalBalance = (): number => {
    const openingAmount = data.openingAmount || 0;
    const totalSales = data.totalSales || 0;
    const totalMovements = calculateTotalMovements();
    
    return openingAmount + totalSales + totalMovements;
  };

  return (
    <Card>
      <CardContent className="pt-6">
        {/* Layout dividido: Cards estadísticos a la izquierda, leyendas a la derecha */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sección izquierda: Cards estadísticos en 3x2 */}
          <div className="flex-1">
            {/* Primera fila: Saldo Inicial, Total Vendido, Num. Ventas */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-gray-600">Saldo Inicial</p>
                <p className="text-xl font-bold text-yellow-600">
                  {formatCurrency(data.openingAmount || 0)}
                </p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600">Total Vendido</p>
                <p className="text-xl font-bold text-green-600">
                  {formatCurrency(data.totalSales || 0)}
                </p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">Num. Ventas</p>
                <p className="text-xl font-bold text-blue-600">
                  {data.salesCount || 0}
                </p>
              </div>
            </div>

            {/* Segunda fila: Saldo Final, Ventas Anuladas, Comprobantes Anulados */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-gray-600">Saldo Final</p>
                <p className="text-xl font-bold text-purple-600">
                  {formatCurrency(calculateFinalBalance())}
                </p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-600">Total Ventas Anuladas</p>
                <p className="text-xl font-bold text-red-600">
                  {formatCurrency(data.cancelledSalesAmount || 0)}
                </p>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <p className="text-sm text-gray-600">Num. Ventas Anuladas</p>
                <p className="text-xl font-bold text-orange-600">
                  {data.cancelledVouchersCount || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Sección derecha: Leyendas de métodos de pago */}
          <div className="lg:w-80 bg-gray-50 rounded-lg p-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Efectivo:</span>
                <span className="font-semibold text-lg">{formatCurrency(data.cashSales || 0)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Tarjeta:</span>
                <span className="font-semibold text-lg">{formatCurrency(data.cardSales || 0)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Yape/Plin:</span>
                <span className="font-semibold text-lg">{formatCurrency(data.yapePlinSales || 0)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Delivery:</span>
                <span className="font-semibold text-lg">{formatCurrency(data.appSales || 0)}</span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};